import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { UserPlus, X, Users } from 'lucide-react';
import { usePodcastEpisodeGuests, useAddEpisodeGuest, useRemoveEpisodeGuest, useUpdateEpisodeGuest } from '@/hooks/usePodcastEpisodeGuests';
import { usePodcastGuests } from '@/hooks/usePodcastGuests';
import { PodcastGuest } from '@/types/podcastHost';

interface EpisodeGuestsManagerProps {
  episodeId: string;
  brandId: string;
  mainGuestId?: string; // Exclude from dropdown
}

const ROLE_OPTIONS = [
  { value: 'main_guest', label: 'Main Guest' },
  { value: 'co_guest', label: 'Co-Guest' },
  { value: 'panelist', label: 'Panelist' },
];

const STATUS_OPTIONS = [
  { value: 'invited', label: 'Invited' },
  { value: 'confirmed', label: 'Confirmed' },
  { value: 'declined', label: 'Declined' },
  { value: 'appeared', label: 'Appeared' },
];

export const EpisodeGuestsManager = ({ episodeId, brandId, mainGuestId }: EpisodeGuestsManagerProps) => {
  const [selectedGuestId, setSelectedGuestId] = useState<string>('');
  const [selectedRole, setSelectedRole] = useState<'main_guest' | 'co_guest' | 'panelist'>('co_guest');
  
  const { data: episodeGuests = [], isLoading: loadingEpisodeGuests } = usePodcastEpisodeGuests(episodeId);
  const { data: allGuests = [] } = usePodcastGuests(brandId);
  const addGuest = useAddEpisodeGuest();
  const removeGuest = useRemoveEpisodeGuest();
  const updateGuest = useUpdateEpisodeGuest();
  
  // Filter out guests already added + main guest
  const addedGuestIds = episodeGuests.map(eg => eg.guest_id);
  const availableGuests = allGuests.filter(
    g => !addedGuestIds.includes(g.id) && g.id !== mainGuestId
  );
  
  const handleAddGuest = async () => {
    if (!selectedGuestId) return;
    
    await addGuest.mutateAsync({
      episodeId,
      guestId: selectedGuestId,
      brandId,
      role: selectedRole,
    });
    
    setSelectedGuestId('');
  };
  
  const handleRemoveGuest = async (id: string) => {
    await removeGuest.mutateAsync({ id, episodeId });
  };
  
  const handleRoleChange = async (id: string, role: string) => {
    await updateGuest.mutateAsync({
      id,
      episodeId,
      updates: { role: role as any },
    });
  };
  
  const handleStatusChange = async (id: string, status: string) => {
    await updateGuest.mutateAsync({
      id,
      episodeId,
      updates: { status: status as any },
    });
  };
  
  const getGuestInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };
  
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Users className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">Additional Guests</span>
      </div>
      
      {/* Current guests list */}
      {loadingEpisodeGuests ? (
        <div className="text-sm text-muted-foreground">Loading guests...</div>
      ) : episodeGuests.length > 0 ? (
        <div className="space-y-2">
          {episodeGuests.map((eg) => (
            <div 
              key={eg.id} 
              className="flex items-center gap-3 p-2 rounded-lg border bg-muted/30"
            >
              <Avatar className="h-8 w-8">
                <AvatarImage src={eg.guest?.image_url} />
                <AvatarFallback className="text-xs">
                  {eg.guest?.name ? getGuestInitials(eg.guest.name) : '?'}
                </AvatarFallback>
              </Avatar>
              
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">
                  {eg.guest?.name || 'Unknown Guest'}
                </div>
                {eg.guest?.company && (
                  <div className="text-xs text-muted-foreground truncate">
                    {eg.guest.job_title && `${eg.guest.job_title} at `}{eg.guest.company}
                  </div>
                )}
              </div>
              
              <Select
                value={eg.role || 'co_guest'}
                onValueChange={(v) => handleRoleChange(eg.id, v)}
              >
                <SelectTrigger className="w-28 h-7 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value} className="text-xs">
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select
                value={eg.status || 'confirmed'}
                onValueChange={(v) => handleStatusChange(eg.id, v)}
              >
                <SelectTrigger className="w-24 h-7 text-xs">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value} className="text-xs">
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7 text-muted-foreground hover:text-destructive"
                onClick={() => handleRemoveGuest(eg.id)}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      ) : (
        <div className="text-sm text-muted-foreground py-2">
          No additional guests added yet
        </div>
      )}
      
      {/* Add guest form */}
      {availableGuests.length > 0 && (
        <div className="flex gap-2 items-center">
          <Select
            value={selectedGuestId}
            onValueChange={setSelectedGuestId}
          >
            <SelectTrigger className="flex-1">
              <SelectValue placeholder="Select a guest to add..." />
            </SelectTrigger>
            <SelectContent>
              {availableGuests.map((guest) => (
                <SelectItem key={guest.id} value={guest.id}>
                  {guest.name} {guest.company ? `(${guest.company})` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Select
            value={selectedRole}
            onValueChange={(v) => setSelectedRole(v as any)}
          >
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ROLE_OPTIONS.map(opt => (
                <SelectItem key={opt.value} value={opt.value}>
                  {opt.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button
            size="sm"
            onClick={handleAddGuest}
            disabled={!selectedGuestId || addGuest.isPending}
          >
            <UserPlus className="h-4 w-4 mr-1" />
            Add
          </Button>
        </div>
      )}
      
      {availableGuests.length === 0 && allGuests.length > 0 && (
        <div className="text-xs text-muted-foreground">
          All guests have been added to this episode
        </div>
      )}
    </div>
  );
};
